/**
 * Este módulo implementa un contexto de React para el manejo global del estado
 * de autenticación en toda la aplicación. Utiliza useReducer para gestionar
 * estados complejos y proporciona funciones para login, registro, logout y
 * verificación de roles de usuario.
 * 
 * Características principales:
 * - Estado global de autenticación accesible desde cualquier componente
 * - Manejo de estados de carga y errores
 * - Funciones integradas para todas las operaciones de autenticación
 * - Verificación automática de sesión al inicializar la aplicación
 * - Manejo de roles y permisos de usuario
 * - Integración completa con el servicio de autenticación
 * 
 * Patrón de diseño: Context + Reducer para estado global
 * 
 * @author Ana Lizcano
 * @version 2.0.0
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService';

/**
 * Contexto de autenticación
 * 
 * Proporciona el estado y las funciones de autenticación a todos los
 * componentes hijos que lo consuman a través del hook useAuth.
 */
const AuthContext = createContext();

/**
 * Reducer para manejar el estado de autenticación
 * 
 * Gestiona todas las transiciones de estado relacionadas con autenticación,
 * incluyendo login, registro, logout, carga de usuario y manejo de errores.
 * 
 * @param {Object} state - Estado actual de autenticación
 * @param {Object} action - Acción a ejecutar con type y payload
 * @returns {Object} Nuevo estado después de aplicar la acción
 */
const authReducer = (state, action) => {
  switch (action.type) {
    /**
     * Inicia el proceso de login
     * Activa el estado de carga y limpia errores previos
     */
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null
      };

    /**
     * Login exitoso
     * Establece el usuario, marca como autenticado y limpia estados de error/carga
     */
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        user: action.payload.user,
        isAuthenticated: true,
        error: null,
        role: action.payload.user.role // Establecer el rol del usuario
      };

    /**
     * Login fallido
     * Limpia datos de usuario, marca como no autenticado y establece error
     */
    case 'LOGIN_FAILURE':
      return {
        ...state,
        loading: false,
        user: null,
        isAuthenticated: false,
        error: action.payload
      };

    /**
     * Inicia el proceso de registro
     * Activa el estado de carga y limpia errores previos
     */
    case 'REGISTER_START':
      return {
        ...state,
        loading: true,
        error: null
      };

    /**
     * Registro exitoso
     * Establece el usuario, marca como autenticado y limpia estados de error/carga
     */
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        loading: false,
        user: action.payload.user,
        isAuthenticated: true,
        error: null
      };

    /**
     * Registro fallido
     * Limpia datos de usuario, marca como no autenticado y establece error
     */
    case 'REGISTER_FAILURE':
      return {
        ...state,
        loading: false,
        user: null,
        isAuthenticated: false,
        error: action.payload
      };

    /**
     * Logout del usuario
     * Limpia todos los datos de autenticación y resetea el estado
     */
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        role: null // Limpiar el rol al cerrar sesión
      };

    /**
     * Carga datos de usuario existente
     * Utilizado al inicializar la aplicación para verificar sesión activa
     */
    case 'LOAD_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload, // Convierte a boolean
        loading: false,
        role: action.payload ? action.payload.role : null // Establecer el rol al cargar el usuario
      };

    /**
     * Limpia errores del estado
     * Útil para resetear mensajes de error después de mostrarlos
     */
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    /**
     * Establece el estado de carga manualmente
     * Permite controlar el estado de loading desde componentes externos
     */
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };

    /**
     * Caso por defecto - retorna el estado sin cambios
     */
    default:
      return state;
  }
};

/**
 * Estado inicial del contexto de autenticación
 * 
 * Define los valores por defecto cuando la aplicación se inicializa
 */
const initialState = {
  user: null,              // Datos del usuario autenticado
  isAuthenticated: false,  // Estado de autenticación
  loading: true,           // Estado de carga (true al inicio para verificar sesión)
  error: null,             // Mensajes de error
  role: null               // Rol del usuario actual
};

/**
 * 
 * Componente que envuelve la aplicación y proporciona el estado y funciones
 * de autenticación a todos los componentes hijos.
 * 
 * @param {Object} props - Props del componente
 * @param {React.ReactNode} props.children - Componentes hijos
 * @returns {JSX.Element} Proveedor del contexto con valor configurado
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  /**
   * Efecto para inicializar el estado de autenticación
   * 
   * Se ejecuta al montar el componente y verifica si existe una sesión
   * activa en localStorage, cargando los datos del usuario si es necesario.
   */
  useEffect(() => {
    /**
     * Función para inicializar el estado de autenticación
     * 
     * Verifica si hay un usuario y token válidos en localStorage
     * y actualiza el estado del contexto en consecuencia.
     */
    const initializeAuth = () => {
      const user = authService.getCurrentUser();
      const isAuthenticated = authService.isAuthenticated();
      
      if (user && isAuthenticated) {
        // Usuario válido encontrado - cargar en el estado
        dispatch({ type: 'LOAD_USER', payload: user });
      } else {
        // No hay sesión válida - establecer como no autenticado
        dispatch({ type: 'LOAD_USER', payload: null });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Función para iniciar sesión
   * 
   * Maneja el proceso completo de login incluyendo estados de carga,
   * éxito y error. Utiliza el servicio de autenticación para la
   * comunicación con el backend.
   * 
   * @param {Object} credentials - Credenciales del usuario
   * @param {string} credentials.username - Nombre de usuario o email
   * @param {string} credentials.password - Contraseña del usuario
   * @returns {Promise<Object>} Respuesta del login con datos del usuario
   * @throws {Error} Error si falla la autenticación
   * 
   * @example
   * try {
   *   await login({ username: 'user@email.com', password: 'password123' });
   *   // Usuario autenticado exitosamente
   * } catch (error) {
   *   // Manejar error de autenticación
   * }
   */
  const login = async (credentials) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authService.login(credentials);
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
      return response;
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
      throw error;
    }
  };


  /**
   * Función para cerrar sesión
   * 
   * Cierra la sesión del usuario tanto en el cliente como en el servidor.
   * Limpia todos los datos de autenticación del estado y localStorage.
   * Garantiza el logout incluso si hay errores en el servidor.
   * 
   * @returns {Promise<void>}
   * 
   * @example
   * await logout();
   * // Usuario desconectado y redirigido
   */
  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Forzar logout local incluso si falla en el servidor
      dispatch({ type: 'LOGOUT' });
    }
  };

  /**
   * Función para limpiar errores
   * 
   * Limpia los mensajes de error del estado. Útil para resetear
   * errores después de mostrarlos al usuario.
   * 
   * @example
   * clearError(); // Limpia cualquier error mostrado
   */
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };


  /**
   * Valor que se proporciona a través del contexto
   * 
   * Incluye todo el estado de autenticación y las funciones disponibles
   * para los componentes que consuman este contexto.
   */
  const value = {
    // Estado de autenticación
    ...state,
    
    // Funciones de autenticación
    login,
    logout,
    clearError,
    
    // Funciones de verificación de roles (delegadas al servicio)
    hasRole: authService.hasRole,
    hasAnyRole: authService.hasAnyRole
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Validación de props para AuthProvider
 */
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook personalizado para usar el contexto de autenticación
 * 
 * Proporciona una forma conveniente de acceder al estado y funciones
 * de autenticación desde cualquier componente. Incluye validación
 * para asegurar que se use dentro de un AuthProvider.
 * 
 * @returns {Object} Estado y funciones de autenticación
 * @throws {Error} Error si se usa fuera de un AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;